/* istanbul ignore file */
import { FormSection, FormRow, BigFormRow } from 'framework/ui/layout';
import {
  Checkbox,
  DateInput,
  TextArea,
  TextInput,
  WysiwygInput,
  ClearAfterUnmountWrapper,
  useInputHelper,
} from 'framework/form';
import { BudgetCategoryDropdown } from 'domain/shared/budget';
import useAssetService, {
  ASSET_STATUS,
  isAutoRenewSubscription as initialIsAutoRenewSubscription,
  isExpandedEquipmentCategory as initialIsExpandedEquipmentCategory,
  isIntangibleAsset as initialIsIntangibleAsset,
  isSubscription as initialIsSubscription,
} from 'domain/finance/assets/useAssetService';
import AssetCategoryDropdown from 'domain/finance/assets/shared/input/AssetCategoryDropdown';
import AssetCategoryTypeDropdown from 'domain/finance/assets/shared/input/AssetCategoryTypeDropdown';
import AssetConditionDropdown from 'domain/finance/assets/shared/input/AssetConditionDropdown';
import { AccountInput } from 'domain/shared/ledger';
import { CURRENCY_TYPES } from 'domain/shared/core/constants';
import { InvoiceTotalInput } from 'domain/shared/core/invoice';
import { CurrencyDropdown } from 'domain/shared/core/input';
import AssetUsefulLifeInput from 'domain/finance/assets/shared/input/AssetUsefulLifeInput';
import AssetBillingCycleInput from 'domain/finance/assets/shared/input/AssetBillingCycleInput';
import { Box } from '@mui/material';
import { isBudgetCategoryRequired } from 'domain/shared/budget';
import { NotDismissedEmployeeInput, OfficeCodeDropdown } from 'domain/shared/employee';

const { EUR, ...ASSET_CURRENCY_TYPES } = CURRENCY_TYPES || {};

const isExpandedEquipmentCategory = (data: any) => initialIsExpandedEquipmentCategory(data.category);

const isIntangibleAsset = (data: any) => initialIsIntangibleAsset(data.category);

const isSubscription = (data: any) => initialIsSubscription(data.type);

const isAutoRenewSubscription = (data: any) => initialIsAutoRenewSubscription(data.subscriptionDetails);

const AssetForm = ({ isEditForm }: { isEditForm?: boolean }) => {
  const { getBalanceAccounts, getExpenseAccounts } = useAssetService();
  const { getValue: getStatus } = useInputHelper({ source: 'status' });
  const status = getStatus();
  const { getValue: getExpenseAccountId } = useInputHelper({ source: 'attribution.expenseAccountId' });
  const expenseAccountId = getExpenseAccountId();
  const { getValue: getPurchaseDate } = useInputHelper<string>({ source: 'purchaseDate' });
  const purchaseDate = getPurchaseDate();
  const isAllowToEditAllFields = status === ASSET_STATUS.ON_BALANCE || status === ASSET_STATUS.WRITE_OFF_AFTER_PURCHASE;

  return (
    <>
      <FormSection title="Asset details">
        <FormRow>
          <TextInput source="inventoryNumber" label="Inventory number" isVisible={!isEditForm} />
          <AssetCategoryDropdown
            disabled={isEditForm && !isAllowToEditAllFields}
            source="category"
            label="Category"
            placeholder="Select Category"
          />
          <ClearAfterUnmountWrapper isVisible={(data: any) => data.category}>
            <AssetCategoryTypeDropdown
              disabled={isEditForm && !isAllowToEditAllFields}
              source="type"
              label="Type"
              categorySource="category"
              placeholder="Select Type"
            />
          </ClearAfterUnmountWrapper>
          <OfficeCodeDropdown
            disabled={isEditForm && !isAllowToEditAllFields}
            source="office"
            filterOptions={({ label }: { label: string }) => !['Tbilisi'].includes(label)}
          />
        </FormRow>

        <FormRow>
          <TextInput disabled={isEditForm && !isAllowToEditAllFields} source="name" label="Name" />
          <TextInput disabled={isEditForm && !isAllowToEditAllFields} source="serialNumber" label="Serial number" />
          <ClearAfterUnmountWrapper isVisible={isExpandedEquipmentCategory}>
            <AssetConditionDropdown source="condition" label="Condition" />
          </ClearAfterUnmountWrapper>
          <TextInput source="location" />
        </FormRow>

        <BigFormRow>
          <TextArea source="characteristics" />
        </BigFormRow>
      </FormSection>

      <FormSection title="Financial information">
        <FormRow>
          <AccountInput
            disabled={isEditForm && !isAllowToEditAllFields}
            source="attribution.balanceAccountId"
            label="Balance account"
            placeholder="Select balance account"
            loadDataMethod={getBalanceAccounts}
            cacheKey="balance-accounts"
          />
          <AccountInput
            disabled={isEditForm && !isAllowToEditAllFields}
            source="attribution.expenseAccountId"
            label="Expense account"
            placeholder="Select expense account"
            loadDataMethod={getExpenseAccounts}
            cacheKey="expense-accounts"
          />
          <ClearAfterUnmountWrapper
            isVisible={(data: any) => !!data?.purchaseDate && !!data?.attribution?.expenseAccountId}
          >
            <BudgetCategoryDropdown
              disabled={(isEditForm && !isAllowToEditAllFields) || !isBudgetCategoryRequired(expenseAccountId)}
              source="attribution.budgetCategory"
              label="Budget category"
              dateValue={purchaseDate}
              accountIdValue={expenseAccountId}
            />
          </ClearAfterUnmountWrapper>
        </FormRow>

        <FormRow>
          <InvoiceTotalInput
            disabled={isEditForm && !isAllowToEditAllFields}
            source="cost.amount"
            currencySource="cost.currency"
            label="Cost amount"
          />
          <CurrencyDropdown
            disabled={isEditForm && !isAllowToEditAllFields}
            source="cost.currency"
            label="Cost currency"
            placeholder=""
            options={ASSET_CURRENCY_TYPES}
          />
        </FormRow>

        <FormRow>
          <DateInput disabled={isEditForm && !isAllowToEditAllFields} label="Purchase date" source="purchaseDate" />
          <ClearAfterUnmountWrapper isVisible={isIntangibleAsset}>
            <DateInput
              disabled={isEditForm && !isAllowToEditAllFields}
              label="Validity start date"
              source="validityStartDate"
            />
          </ClearAfterUnmountWrapper>
          <ClearAfterUnmountWrapper isVisible={isIntangibleAsset}>
            <DateInput
              disabled={isEditForm && !isAllowToEditAllFields}
              label="Validity end date"
              source="validityEndDate"
            />
          </ClearAfterUnmountWrapper>
          <ClearAfterUnmountWrapper isVisible={isExpandedEquipmentCategory}>
            <DateInput
              disabled={isEditForm && !isAllowToEditAllFields}
              label="Warranty end date"
              source="warrantyEndDate"
            />
          </ClearAfterUnmountWrapper>
        </FormRow>
        <FormRow>
          <AssetUsefulLifeInput
            disabled={isEditForm && !isAllowToEditAllFields}
            source="customUsefulLifePeriod"
            label="Useful life"
            categorySource="category"
            typeSource="type"
            isEditForm={isEditForm}
          />
        </FormRow>
      </FormSection>

      <FormSection title="Subscription details" isVisible={isSubscription}>
        <FormRow>
          <ClearAfterUnmountWrapper>
            <Checkbox
              disabled={isEditForm && !isAllowToEditAllFields}
              source="subscriptionDetails.isAutoRenew"
              label="Is auto-renew?"
            />
          </ClearAfterUnmountWrapper>
        </FormRow>

        <FormRow>
          <ClearAfterUnmountWrapper isVisible={isAutoRenewSubscription}>
            <DateInput
              disabled={isEditForm && !isAllowToEditAllFields}
              label="Next payment date"
              source="subscriptionDetails.nextPaymentDate"
            />
          </ClearAfterUnmountWrapper>
          <ClearAfterUnmountWrapper isVisible={(data: any) => !isAutoRenewSubscription(data)}>
            <DateInput
              disabled={isEditForm && !isAllowToEditAllFields}
              label="Expiry date"
              source="subscriptionDetails.expiryDate"
            />
          </ClearAfterUnmountWrapper>
          <ClearAfterUnmountWrapper>
            <AssetBillingCycleInput
              disabled={isEditForm && !isAllowToEditAllFields}
              source="subscriptionDetails.billingCycle"
              label="Billing cycle"
            />
          </ClearAfterUnmountWrapper>
        </FormRow>
      </FormSection>

      <FormSection title="Assignment details" isVisible={!isEditForm}>
        <FormRow>
          <NotDismissedEmployeeInput
            disabled={isEditForm && !isAllowToEditAllFields}
            source="assignmentDetails.currentOwner"
            nameSource="assignmentDetails.currentOwnerFullName"
            label="Current owner"
            includeContractors
          />
          <ClearAfterUnmountWrapper isVisible={(data: any) => !!data?.assignmentDetails?.currentOwner}>
            <DateInput
              disabled={isEditForm && !isAllowToEditAllFields}
              source="assignmentDetails.provisionDate"
              label="Provision date"
            />
          </ClearAfterUnmountWrapper>
        </FormRow>
      </FormSection>

      <FormSection title="Note" isVisible={!isEditForm}>
        <BigFormRow>
          <Box sx={{ '.label__wrapper': { display: 'none' } }}>
            <WysiwygInput disabled={isEditForm && !isAllowToEditAllFields} source="note.content" label="Note" />
          </Box>
        </BigFormRow>
      </FormSection>
    </>
  );
};

export default AssetForm;
